import React, { useContext, useMemo } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { BASE_URL_API } from "../../References/urls";
import AuthContext from "../../store/auth-context";
import styles from './TagSelector.module.css'

const TagSelector = ({ masterTags = [], selectedTags, onChange, contentId }) => {
    const authCtx = useContext(AuthContext)

    /** 🧠 Normalize selectedTags to always be an array of strings */
    const normalizedSelectedTags = useMemo(() => {
        if (!selectedTags) return [];
        if (Array.isArray(selectedTags)) return selectedTags.filter(Boolean);

        if (typeof selectedTags === "string") {
            try {
                const parsed = JSON.parse(selectedTags);
                if (Array.isArray(parsed)) return parsed.filter(Boolean);
                return selectedTags.split(",").map(t => t.trim()).filter(Boolean);
            } catch {
                return selectedTags.split(",").map(t => t.trim()).filter(Boolean);
            }
        }

        return [];
    }, [selectedTags]);

    /** Convert master tags into react-select option format */
    const options = useMemo(
        () => masterTags.map(tag => ({ value: tag.label, label: tag.label })),
        [masterTags]
    );

    /** Convert selected tags into option format */
    const selectedOptions = normalizedSelectedTags.map(tag => ({ value: tag, label: tag }));

    /** When user selects or removes a tag */
    const handleChange = async (selected) => {
        const updated = selected ? selected.map(opt => opt.value) : [];
        onChange?.(contentId, updated);

        const response = await fetch(BASE_URL_API + "/updateContentTag",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    updatedBy: authCtx?.userIdentifier,
                    contentId: contentId,
                    selectedTags: JSON.stringify(updated)
                })
            })

        if (!response.ok) {
            console.log("Something went wrong : Server error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                toast("Successfully updated")
            } else {
                console.log(respo)
            }
        }
    };

    return (
        <div style={{ maxWidth: 400, minWidth: 200 }}>
            <Select
                className={styles.mainContainer}
                isMulti
                options={options}
                value={selectedOptions}
                onChange={handleChange}
                placeholder="Select tags..."
                menuPortalTarget={document.body}  // 👈 renders dropdown outside of parent bounds
                styles={{
                    control: (base) => ({
                        ...base,
                        borderColor: "#ccc",
                        borderRadius: "8px",
                        minHeight: "40px",
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999, // ensures it's on top of all modals etc.
                    }),
                    multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#e0f2ff",
                    }),
                    multiValueLabel: (base) => ({
                        ...base,
                        color: "#0077b6",
                        fontWeight: 500,
                    }),
                    multiValueRemove: (base) => ({
                        ...base,
                        color: "#0077b6",
                        cursor: "pointer",
                        ":hover": { backgroundColor: "#cce7ff", color: "#004e89" },
                    }),
                }}
                menuPosition="absolute"  // ensure correct positioning with portal
                menuShouldScrollIntoView={false}
                noOptionsMessage={() => "No matching tags"}
            />

        </div>
    );
};

export default TagSelector;
